// Messagerie Client-Vendeur - Espace Client
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquare, Search, Send, Paperclip, Image, MoreVertical,
  Phone, Video, Archive, Star, Trash2, ChevronLeft, Check, CheckCheck
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

import { API_URL } from '../../config/api';

// Liste des conversations
function ConversationList({ conversations, selectedId, onSelect, searchQuery, setSearchQuery }) {
  const filteredConversations = conversations.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.lastMessage.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="p-4 border-b border-gray-100">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Rechercher..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-xl bg-gray-50"
          />
        </div>
      </div>

      {/* Conversations */}
      <div className="flex-1 overflow-y-auto">
        {filteredConversations.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            <MessageSquare size={32} className="mx-auto mb-2 text-gray-300" />
            <p className="text-sm">Aucune conversation</p>
          </div>
        ) : (
          filteredConversations.map((conv) => (
            <button
              key={conv.id}
              onClick={() => onSelect(conv)}
              className={`w-full flex items-start gap-3 p-4 hover:bg-gray-50 transition-colors border-b border-gray-50 ${
                selectedId === conv.id ? 'bg-orange-50 border-l-4 border-l-orange-500' : ''
              }`}
            >
              <div className="relative flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                  <span className="text-white font-semibold">{conv.name.charAt(0)}</span>
                </div>
                {conv.online && (
                  <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                )}
              </div>
              <div className="flex-1 min-w-0 text-left">
                <div className="flex items-center justify-between mb-1">
                  <p className="font-semibold text-gray-900 truncate">{conv.name}</p>
                  <span className="text-xs text-gray-400 flex-shrink-0">{conv.time}</span>
                </div>
                <p className={`text-sm truncate ${conv.unread > 0 ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                  {conv.lastMessage}
                </p>
                {conv.orderId && (
                  <span className="inline-block mt-1 px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                    Commande #{conv.orderId}
                  </span>
                )}
              </div>
              {conv.unread > 0 && (
                <span className="w-5 h-5 rounded-full bg-orange-500 text-white text-xs flex items-center justify-center flex-shrink-0">
                  {conv.unread}
                </span>
              )}
            </button>
          ))
        )}
      </div>
    </div>
  );
}

// Zone de chat
function ChatArea({ conversation, messages, onSendMessage }) {
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (!newMessage.trim()) return;
    onSendMessage(newMessage);
    setNewMessage('');
  };

  if (!conversation) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <MessageSquare size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">Sélectionnez une conversation</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3 bg-white">
        <div className="relative">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
            <span className="text-white font-semibold">{conversation.name.charAt(0)}</span>
          </div>
          {conversation.online && (
            <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white" />
          )}
        </div>
        <div className="flex-1">
          <p className="font-semibold text-gray-900">{conversation.name}</p>
          <p className="text-xs text-gray-500">
            {conversation.online ? 'En ligne' : `Vu ${conversation.lastSeen || 'récemment'}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="p-2 hover:bg-gray-100 rounded-full">
            <Phone size={18} className="text-gray-500" />
          </button>
          <button className="p-2 hover:bg-gray-100 rounded-full">
            <MoreVertical size={18} className="text-gray-500" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {/* Date separator */}
        <div className="flex items-center justify-center">
          <span className="px-3 py-1 bg-white rounded-full text-xs text-gray-500 shadow-sm">
            Aujourd'hui
          </span>
        </div>

        {messages.map((msg, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex ${msg.isMe ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-[75%] ${msg.isMe ? 'order-2' : ''}`}>
              <div className={`px-4 py-2.5 rounded-2xl ${
                msg.isMe 
                  ? 'bg-orange-500 text-white rounded-tr-sm' 
                  : 'bg-white text-gray-900 rounded-tl-sm shadow-sm'
              }`}>
                <p className="text-sm">{msg.text}</p>
              </div>
              <div className={`flex items-center gap-1 mt-1 ${msg.isMe ? 'justify-end' : ''}`}>
                <span className="text-xs text-gray-400">{msg.time}</span>
                {msg.isMe && (
                  msg.read 
                    ? <CheckCheck size={14} className="text-blue-500" />
                    : <Check size={14} className="text-gray-400" />
                )}
              </div>
            </div>
          </motion.div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-200 bg-white">
        <div className="flex items-center gap-2">
          <button className="p-2 hover:bg-gray-100 rounded-full">
            <Paperclip size={20} className="text-gray-500" />
          </button>
          <button className="p-2 hover:bg-gray-100 rounded-full">
            <Image size={20} className="text-gray-500" />
          </button>
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Écrire un message..."
            className="flex-1 rounded-full bg-gray-50"
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
          />
          <Button
            onClick={handleSend}
            disabled={!newMessage.trim()}
            className="w-10 h-10 rounded-full bg-orange-500 hover:bg-orange-600 p-0"
          >
            <Send size={18} />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function MessagesPage() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showList, setShowList] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
    }
  }, [selectedConversation]);

  const fetchConversations = async () => {
    setLoading(true);
    try {
      // Données simulées
      setConversations([
        {
          id: '1',
          name: 'Marie Lambert',
          lastMessage: 'Bonjour, est-ce que le produit est toujours disponible ?',
          time: '14:32',
          unread: 2,
          online: true,
          orderId: 'CMD-2024-001'
        },
        {
          id: '2',
          name: 'Pierre Durand',
          lastMessage: 'Merci pour la livraison rapide !',
          time: '09:15',
          unread: 0,
          online: false,
          lastSeen: 'il y a 2h',
          orderId: 'CMD-2024-002'
        },
        {
          id: '3',
          name: 'Sophie Martin',
          lastMessage: 'Est-ce que tu fais des devis pour les professionnels ?',
          time: 'Hier',
          unread: 1,
          online: false,
          lastSeen: 'hier'
        },
        {
          id: '4',
          name: 'Jean Petit',
          lastMessage: 'D\'accord, je comprends. Merci !',
          time: 'Lun',
          unread: 0,
          online: true
        },
      ]);
    } catch (e) {
      console.error('Error fetching conversations:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    // Simuler des messages
    const mockMessages = {
      '1': [
        { text: 'Bonjour ! Je suis intéressé par la perceuse Bosch.', time: '14:20', isMe: false },
        { text: 'Bonjour Marie ! Oui, elle est disponible. Tu as des questions ?', time: '14:25', isMe: true, read: true },
        { text: 'Est-ce qu\'elle est livrée avec la batterie ?', time: '14:28', isMe: false },
        { text: 'Oui, la batterie 18V et le chargeur sont inclus.', time: '14:30', isMe: true, read: true },
        { text: 'Bonjour, est-ce que le produit est toujours disponible ?', time: '14:32', isMe: false },
      ],
      '2': [
        { text: 'Bonjour, j\'ai bien reçu ma commande.', time: '09:00', isMe: false },
        { text: 'Parfait ! Tout est en ordre ?', time: '09:10', isMe: true, read: true },
        { text: 'Merci pour la livraison rapide !', time: '09:15', isMe: false },
      ],
      '3': [
        { text: 'Bonjour, je suis artisan plombier.', time: 'Hier 16:00', isMe: false },
        { text: 'Bonjour ! Comment puis-je t\'aider ?', time: 'Hier 16:15', isMe: true, read: true },
        { text: 'Est-ce que tu fais des devis pour les professionnels ?', time: 'Hier 16:20', isMe: false },
      ],
      '4': [
        { text: 'Bonjour, le délai de livraison est de combien ?', time: 'Lun 10:00', isMe: false },
        { text: 'Bonjour ! Comptez 2-3 jours ouvrés.', time: 'Lun 10:30', isMe: true, read: true },
        { text: 'D\'accord, je comprends. Merci !', time: 'Lun 10:35', isMe: false },
      ],
    };
    setMessages(mockMessages[conversationId] || []);
  };

  const handleSendMessage = (text) => {
    const newMsg = {
      text,
      time: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
      isMe: true,
      read: false
    };
    setMessages([...messages, newMsg]);
    
    // Mettre à jour la conversation
    setConversations(conversations.map(c => 
      c.id === selectedConversation.id 
        ? { ...c, lastMessage: text, time: 'À l\'instant', unread: 0 }
        : c
    ));
  };

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    // Marquer comme lu
    setConversations(conversations.map(c => 
      c.id === conv.id ? { ...c, unread: 0 } : c
    ));
    // Mobile: masquer la liste
    if (window.innerWidth < 768) {
      setShowList(false);
    }
  };

  return (
    <div className="h-[calc(100vh-120px)] flex bg-white rounded-2xl border border-gray-200 overflow-hidden" data-testid="messages-page">
      {/* Liste des conversations (desktop always visible, mobile toggle) */}
      <div className={`${
        showList ? 'flex' : 'hidden md:flex'
      } flex-col w-full md:w-80 lg:w-96 border-r border-gray-200`}>
        <div className="p-4 border-b border-gray-100">
          <h2 className="font-bold text-lg text-gray-900">Messages</h2>
          <p className="text-sm text-gray-500">
            {conversations.filter(c => c.unread > 0).length} non lu(s)
          </p>
        </div>
        <ConversationList
          conversations={conversations}
          selectedId={selectedConversation?.id}
          onSelect={handleSelectConversation}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />
      </div>

      {/* Zone de chat */}
      <div className={`${
        !showList ? 'flex' : 'hidden md:flex'
      } flex-1 flex-col`}>
        {/* Bouton retour mobile */}
        {selectedConversation && (
          <button
            onClick={() => setShowList(true)}
            className="md:hidden flex items-center gap-2 p-3 border-b border-gray-200 text-gray-600"
          >
            <ChevronLeft size={20} />
            <span>Retour</span>
          </button>
        )}
        <ChatArea
          conversation={selectedConversation}
          messages={messages}
          onSendMessage={handleSendMessage}
        />
      </div>
    </div>
  );
}
